import React, { useState } from 'react';
import styled, { useTheme } from 'styled-components/macro';
import { useHistory } from 'react-router';
import dayjs from 'dayjs';
import { isNull } from 'lodash';
import { calculateRange } from 'lib/utils/functions';
import { theme } from 'lib/style';
import { FaChevronRight } from 'react-icons/fa';
import Select from 'react-select';
import { useAuth } from 'lib/context';
import {
  LoadingIndicator,
  MainContainer,
  Search,
  Table,
  TableFooter,
  TablePaginationNew,
  TablePaginationSizeNew,
  TableContextProvider,
} from 'lib/components';
import { fetchCustomer } from 'lib/api';
import Switch from '../../design/landingPageBuilder/components/Switch';
import { useQuerySmsUsers } from 'lib/api/sms/useQuerySmsUsers';
import { SmsUsers } from 'lib/api/sms/types';
import { getSmsOptions } from 'lib/const';
import { useTranslation } from 'react-i18next';
import { ModalDateRangeSelector } from 'app/pages/reports/components/DateRangeSelector/ModalDateRangeSelector';
import { EXTEND_DELETION_TYPES } from 'lib/const/ModalVideoDeletionApproval';

const FiltersContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${theme.mediaQueryMinWidth.sm} {
    flex-direction: row;
    justify-content: space-between;
  }
`;

const Filter = styled.div`
  float: right;
  display: flex;
  justify-content: space-between;
  ${theme.mediaQueryMinWidth.sm} {
    width: 500px;
  }
`;

const MainWrapper = styled.div`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 16px;
  ${theme.mediaQueryMinWidth.mb} {
    padding: 24px 32px 32px 32px;
  }
  ${theme.mediaQueryMinWidth.lg} {
    max-width: 1064px;
  }
  .tab-container {
    max-width: 100%;
  }
  .tab-content {
    width: 100%;
  }
  .tab-navigation {
    padding: 0;
    margin: 0 32px 0 32px;
    justify-content: left;
    .tab {
      & > div {
        font-size: 18px;
        font-weight: 600;
        line-height: 1.33;
      }
      justify-content: center;
      align-items: center;
      margin-left: 0;
      margin-right: 32px;
    }
  }
`;

const Content = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  ${theme.mediaQueryMaxWidth.md} {
    flex-direction: column;
  }
`;

const Layout = styled.div`
  ${theme.fontNormal500};
  display: flex;
  flex-direction: column;
  text-align: center;
  position: relative;
  width: 100%;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const TableCell = styled.div<{ width: number }>`
  width: ${props => (props.width ? props.width + 'px' : 'auto')};
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
  display: flex;
  align-items: center;
  &.class-red {
    color: red;
  }
  &.class--right {
    justify-content: end;
  }
`;

const FieldToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 400px;
  justify-content: end;
  label {
    padding-right: 10px;
  }
`;

const Default = styled.div`
  background: rgba(0, 27, 83, 0.05);
  padding: 5px 10px 5px 10px;
  font-weight: 600;
  border-radius: 5px;
  color: ${theme.palette.primaryDarkBlue};
  margin-left: 10px;
  font-size: 13px;
`;

const getTableFields = (t: Function) => [
  {
    value: 'firstName',
    label: t('Name'),
  },
  {
    value: 'lastResponse',
    label: t('Last Response'),
  },
  {
    value: 'sentCount',
    label: t('Sent'),
  },
  {
    value: 'receivedCount',
    label: t('Received'),
  },
  {
    value: 'notAnswered',
    label: t('Not answered'),
  },
];

export const UsersLayout = () => {
  const { t } = useTranslation();
  const smsOptions = getSmsOptions(t);
  const [onlyUsersUsingSMS, setOnlyUsersUsingSMS] = React.useState(
    localStorage.getItem('SMS_USER_TOGGLE') == 'true' ? true : false
  );
  const [search, setSearch] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [size, setSize] = React.useState(10);
  const [sent, setSent] = React.useState(smsOptions[1].value);
  const [receive, setReceive] = React.useState(smsOptions[1].value);
  const [defaultUserId, setDefaultUserId] = React.useState('');
  const [sentObj, setSentObj] = useState(calculateRange(sent));
  const [receiveObj, setReceiveObj] = useState(calculateRange(receive));
  const [showSentModal, setShowSentModal] = useState(false);
  const [showReceiveModal, setShowReceiveModal] = useState(false);

  const [customSentStart, setCustomSentStart] = useState<Date | null>(null);
  const [customSentEnd, setCustomSentEnd] = useState<Date | null>(null);

  const [customReceiveStart, setCustomReceiveStart] = useState<Date | null>(
    null
  );
  const [customReceiveEnd, setCustomReceiveEnd] = useState<Date | null>(null);
  const tableFields = getTableFields(t);
  const {
    data: users,
    isLoading: loading,
    refetch: loadSmsUsers,
  } = useQuerySmsUsers({
    page,
    size,
    search,
    onlyUsersUsingSMS,
    sFrom: sentObj.from,
    sTo: sentObj.to,
    rFrom: receiveObj.from,
    rTo: receiveObj.to,
  });
  const {
    userData: { customerId },
  } = useAuth();

  const columnWidths: any = [300];
  const history = useHistory();
  const themes = useTheme();

  const calculateCustomRange = (start: Date, end: Date) => ({
    from: dayjs(start).startOf('day').utc().format(),
    to: dayjs(end).endOf('day').utc().format(),
  });

  const refreshSmsUser = async () => {
    await loadSmsUsers();
  };

  const onSentRangeSelected = (start: Date | null, end: Date | null) => {
    if (!start || !end) return;
    setCustomSentStart(start);
    setCustomSentEnd(end);
    setSent(EXTEND_DELETION_TYPES.CUSTOM);
    setShowSentModal(false);
  };

  const onReceiveRangeSelected = (start: Date | null, end: Date | null) => {
    if (!start || !end) return;
    setCustomReceiveStart(start);
    setCustomReceiveEnd(end);
    setReceive(EXTEND_DELETION_TYPES.CUSTOM);
    setShowReceiveModal(false);
  };

  const onSearch = (query: string) => {
    setSearch(query.toLowerCase());
    setPage(0);
  };
  const onPaginationChange = ({
    page: newPage,
    size: newSize,
  }: {
    page: number;
    size: number;
  }) => {
    setSize(newSize);
    setPage(newSize !== size ? 0 : newPage);
  };

  const calculateDifferenceInTime = (value: any) => {
    const dateCurrent = dayjs();
    const day: any = dateCurrent.diff(value, 'day', true);
    const hour: any = dateCurrent.diff(value, 'hour', true);
    const minute: any = dateCurrent.diff(value, 'minute', true);
    if (parseInt(minute, 10) < 60) {
      return { value: `${parseInt(day, 10)} minute(s) ago`, type: 'minute' };
    }
    if (parseInt(day, 10) > 0) {
      return { value: `${parseInt(day, 10)} day(s) ago`, type: 'day' };
    }
    return { value: `${parseInt(hour, 10)} hour(s) ago`, type: 'hour' };
  };

  const getFieldValue = (dataSet: any, data: string) => {
    if (dataSet.userId != -1) {
      switch (data) {
        case 'lastResponse':
          return !isNull(dataSet[data])
            ? calculateDifferenceInTime(dataSet[data]).value
            : '--';
        case 'sentCount':
        case 'receivedCount':
          return dataSet[data];
        case 'firstName':
          return `${dataSet.firstName} ${dataSet.lastName}`;
        case 'notAnswered':
          return '0';
        default:
          return '--';
      }
    }
    return '--';
  };

  const loadCustomer = async () => {
    const customer = await fetchCustomer(customerId);
    if (customer && customer.defaultSMSUserId) {
      setDefaultUserId(customer.defaultSMSUserId);
    }
  };

  React.useEffect(() => {
    if (
      sent === EXTEND_DELETION_TYPES.CUSTOM &&
      customSentStart &&
      customSentEnd
    ) {
      setSentObj(calculateCustomRange(customSentStart, customSentEnd));
    } else {
      setSentObj(calculateRange(sent));
    }
  }, [sent, customSentStart, customSentEnd]);

  React.useEffect(() => {
    if (
      receive === EXTEND_DELETION_TYPES.CUSTOM &&
      customReceiveStart &&
      customReceiveEnd
    ) {
      setReceiveObj(calculateCustomRange(customReceiveStart, customReceiveEnd));
    } else {
      setReceiveObj(calculateRange(receive));
    }
  }, [receive, customReceiveStart, customReceiveEnd]);

  React.useEffect(() => {
    loadCustomer();
  }, []);

  React.useEffect(() => {
    setPage(0);
  }, [sentObj.from, sentObj.to, receiveObj.from, receiveObj.to]);

  React.useEffect(() => {
    refreshSmsUser();
  }, [sentObj, receiveObj]);

  return (
    <>
      <MainContainer>
        <MainWrapper>
          <FiltersContainer>
            <Filter>
              <h3>{t('Users')}</h3>
            </Filter>
          </FiltersContainer>
          <Content>
            <Layout>
              <Row style={{ marginBottom: 20 }}>
                <div style={{ width: 300 }}>
                  <Search
                    prevSearch={search}
                    placeholder={t('Search by number or name…')}
                    onSearch={onSearch}
                  />
                </div>
                <Row>
                  <FieldToggleWrapper>
                    <label>{t('Show only users using SMS')}</label>
                    <Switch
                      id={'switch-only-sms'}
                      isOn={onlyUsersUsingSMS}
                      onColor={themes.colors.primary[100]}
                      handleToggle={() => {
                        setOnlyUsersUsingSMS(!onlyUsersUsingSMS);
                        localStorage.setItem(
                          'SMS_USER_TOGGLE',
                          JSON.stringify(!onlyUsersUsingSMS)
                        );
                      }}
                    />
                  </FieldToggleWrapper>
                </Row>
              </Row>
              <TableContextProvider
                total={users?.count}
                onChange={onPaginationChange}
                initPage={page}
                initSize={size}
              >
                <div style={{ overflowX: 'auto' }}>
                  {!loading && users?.users?.length ? (
                    <>
                      <Table
                        compact={true}
                        headers={[
                          ...tableFields.map((item, index) => {
                            return (
                              <TableCell
                                width={columnWidths[index + 1]}
                                onClick={() => {}}
                                className={
                                  item.value == 'notAnswered'
                                    ? 'class--right'
                                    : ''
                                }
                              >
                                {item.label}
                                {item.label == t('Sent') ||
                                item.label == t('Received') ? (
                                  <>
                                    &nbsp;
                                    <Select
                                      styles={{
                                        control: (base: any) => ({
                                          ...base,
                                          height: '40px',
                                          width: '130px',
                                        }),
                                        indicatorSeparator: () => ({
                                          display: 'none',
                                        }),
                                      }}
                                      options={smsOptions}
                                      menuPortalTarget={document.body}
                                      menuPlacement={'bottom'}
                                      isSearchable={false}
                                      value={smsOptions.find(o => {
                                        return (
                                          o.value ===
                                          (item.label == 'Sent'
                                            ? sent
                                            : receive)
                                        );
                                      })}
                                      onChange={(option: any) => {
                                        if (item.label === t('Sent')) {
                                          if (
                                            option.value ===
                                            EXTEND_DELETION_TYPES.CUSTOM
                                          ) {
                                            setShowSentModal(true);
                                          } else {
                                            setSent(option.value);
                                          }
                                        } else {
                                          if (
                                            option.value ===
                                            EXTEND_DELETION_TYPES.CUSTOM
                                          ) {
                                            setShowReceiveModal(true);
                                          } else {
                                            setReceive(option.value);
                                          }
                                        }
                                      }}
                                    />{' '}
                                  </>
                                ) : null}
                              </TableCell>
                            );
                          }),
                          <TableCell
                            width={columnWidths[tableFields.length]}
                          ></TableCell>,
                        ]}
                        hoverable={false}
                        columnWidths={columnWidths}
                        rows={users?.users.map(
                          (user: SmsUsers, index: number) => ({
                            key: index,
                            columns: [
                              ...tableFields.map((item, index) => {
                                return (
                                  <TableCell
                                    width={columnWidths[index + 1]}
                                    onClick={() => {}}
                                    className={
                                      item.value == 'notAnswered'
                                        ? `${
                                            getFieldValue(user, item.value) > 0
                                              ? 'class-red'
                                              : ''
                                          } class--right`
                                        : ''
                                    }
                                  >
                                    {
                                      // @ts-ignore
                                      // contact[item.value]
                                      getFieldValue(user, item.value)
                                    }
                                    {item.value == 'firstName' &&
                                      user.userId == defaultUserId && (
                                        <Default>{t('DEFAULT')}</Default>
                                      )}
                                  </TableCell>
                                );
                              }),
                              <TableCell
                                style={{
                                  textAlign: 'right',
                                  paddingLeft: '25px',
                                }}
                                width={columnWidths[tableFields.length]}
                              >
                                <FaChevronRight
                                  size={13}
                                  onClick={() => {
                                    history.push(
                                      `/sms/messages/${user.userId}`
                                    );
                                  }}
                                />
                              </TableCell>,
                            ],
                            onClick: () => {
                              history.push(`/sms/messages/${user.userId}`);
                            },
                          })
                        )}
                      />
                      <TableFooter>
                        <TablePaginationNew />
                        <TablePaginationSizeNew globalName='sms_user_list' />
                      </TableFooter>
                    </>
                  ) : loading ? (
                    <LoadingIndicator isLoading={loading} height='500px' />
                  ) : !users?.users?.length ? (
                    <p
                      style={{
                        fontWeight: 'bold',
                        textAlign: 'center',
                        margin: '36px 0',
                      }}
                    >
                      {t('No matching results found')}.{' '}
                    </p>
                  ) : null}
                </div>
              </TableContextProvider>
            </Layout>
          </Content>
        </MainWrapper>
        {showSentModal && (
          <ModalDateRangeSelector
            initStartDate={customSentStart || new Date()}
            initEndDate={customSentEnd || new Date()}
            handleModalClose={() => setShowSentModal(false)}
            onRangeSelected={onSentRangeSelected}
          />
        )}

        {showReceiveModal && (
          <ModalDateRangeSelector
            initStartDate={customReceiveStart || new Date()}
            initEndDate={customReceiveEnd || new Date()}
            handleModalClose={() => setShowReceiveModal(false)}
            onRangeSelected={onReceiveRangeSelected}
          />
        )}
      </MainContainer>
    </>
  );
};
